import { useState, useEffect } from 'react'
import { BookOpen, ChevronLeft, ClipboardList, Pencil, Scale, Sparkles } from 'lucide-react'
import { JournalList } from './JournalList'
import { JournalEditor } from './JournalEditor'
import { WeeklyReviewWizard } from './WeeklyReviewWizard'
import { CoachingPanel } from './CoachingPanel'
import { SearchView } from './SearchView'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useCreateJournalEntry } from '@/hooks/useJournal'
import type { JournalEntry } from '@/types'

type RightView = 'entry' | 'coach' | 'search'

export function JournalView() {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [rightView, setRightView] = useState<RightView>('entry')
  const [reviewOpen, setReviewOpen] = useState(false)
  const [searchSeed, setSearchSeed] = useState('')
  const createEntry = useCreateJournalEntry()

  useEffect(() => {
    function handler(e: Event) {
      const id = (e as CustomEvent<{ id: string }>).detail.id
      setSelectedId(id)
      setRightView('entry')
    }
    window.addEventListener('plumb:open-entry', handler)
    return () => window.removeEventListener('plumb:open-entry', handler)
  }, [])

  async function handleNew(type: JournalEntry['type'] = 'free') {
    const id = await createEntry.mutateAsync({ type })
    setSelectedId(id)
    setRightView('entry')
  }

  function handleSelectEntry(id: string) {
    setSelectedId(id)
    setRightView('entry')
  }

  function handleOpenCoach() {
    setSelectedId(null)
    setRightView('coach')
  }

  function handleDeepSearch(query: string) {
    setSearchSeed(query)
    setSelectedId(null)
    setRightView('search')
  }

  function handleOpenEntry(id: string) {
    setSelectedId(id)
    setRightView('entry')
  }

  // Mobile drill-down: the list is the "home" screen; any selection or
  // right-view switch shows the detail pane with a back row instead.
  const mobileShowsDetail = selectedId !== null || rightView !== 'entry'

  function handleBack() {
    setSelectedId(null)
    setRightView('entry')
  }

  return (
    <div className="flex h-view min-h-0">
      {/* Left: entry list + action buttons — pinned */}
      <div
        className={cn(
          'w-full md:w-64 flex-shrink-0 flex-col min-h-0 gap-2 md:border-r border-border/60 md:pr-4',
          mobileShowsDetail ? 'hidden md:flex' : 'flex',
        )}
      >
        <JournalList
          selectedId={selectedId}
          onSelect={handleSelectEntry}
          onDeepSearch={handleDeepSearch}
        />

        <div className="grid grid-cols-2 gap-2 md:grid-cols-1">
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenCoach}
            className={cn(
              'gap-1.5 w-full justify-center md:justify-start h-11 md:h-9',
              rightView === 'coach'
                ? 'bg-secondary border-ring text-foreground'
                : 'text-muted-foreground',
            )}
          >
            <Sparkles size={14} />
            Coaching letter
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-muted-foreground w-full justify-center md:justify-start h-11 md:h-9"
            onClick={() => setReviewOpen(true)}
          >
            <ClipboardList size={14} />
            Weekly review
          </Button>
        </div>
      </div>

      {/* Right: search, coach panel, editor, or empty state */}
      <div
        className={cn(
          'flex-1 min-w-0 flex-col min-h-0 md:pl-6',
          mobileShowsDetail ? 'flex' : 'hidden md:flex',
        )}
      >
        {/* Mobile back row */}
        <button
          type="button"
          onClick={handleBack}
          className="md:hidden flex items-center gap-1.5 self-start text-sm text-muted-foreground hover:text-foreground transition-colors py-2.5 pr-3 mb-1 -mt-1"
        >
          <ChevronLeft size={16} />
          Entries
        </button>
        {rightView === 'search' ? (
          <SearchView key={searchSeed} initialQuery={searchSeed} onOpenEntry={handleOpenEntry} />
        ) : rightView === 'coach' ? (
          <CoachingPanel />
        ) : selectedId ? (
          <JournalEditor
            key={selectedId}
            entryId={selectedId}
            onDelete={() => setSelectedId(null)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
            <BookOpen size={32} className="opacity-30" />
            <p className="text-sm">Select an entry or start a new one</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">New entry</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-44">
                <DropdownMenuItem onClick={() => void handleNew('free')}>
                  <Pencil size={13} className="mr-2 text-muted-foreground" />
                  Free entry
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => void handleNew('discernment')}>
                  <Scale size={13} className="mr-2 text-indigo-500" />
                  Discernment
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => void handleNew('weekly_review')}>
                  <ClipboardList size={13} className="mr-2 text-emerald-600" />
                  Weekly review
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <WeeklyReviewWizard open={reviewOpen} onOpenChange={setReviewOpen} />
    </div>
  )
}
